// ChartWindMachine.jsx
import React from 'react'
import PropTypes from 'prop-types'
import { Chart } from 'react-google-charts'
import { injectIntl } from 'react-intl'
import Spinner from '../../../util/spinner-shim'
import _maxBy from 'lodash/maxBy'
import _minBy from 'lodash/minBy'

import {
  fromC,
  rpmToState,
  rpmOrchardRiteAutometer9117,
  chartDimensions,
} from 'altrac-conversions'

// HOC
import withAppStore from '../../../util/withAppStore'

import NoChartData from './NoChartData'
import styles from '../../../../css/charts.module.css'

const formatTooltipValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A'
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return 'N/A'
    try {
      return value.toLocaleString()
    } catch {
      // Some devices/browsers ship with a malformed default Intl locale,
      // which throws "failed to initialize DateTimeFormat" on toLocaleString().
      return String(value)
    }
  }
  return value
}

const formatTooltipDate = (timestamp) => {
  const date = new Date(timestamp)
  try {
    return date.toLocaleString()
  } catch {
    // Some devices/browsers ship with a malformed default Intl locale,
    // which throws "failed to initialize DateTimeFormat" on toLocaleString().
    return date.toISOString()
  }
}

const buildTooltip = (timestamp, entries = []) => {
  const dateString = formatTooltipDate(timestamp)
  const rowsMarkup = entries.length
    ? entries.map(({ label, value }) => `
        <div class="${styles.chartTooltipRow}">
          <span class="${styles.chartTooltipLabel}">${label}</span>
          <span class="${styles.chartTooltipValue}">${value}</span>
        </div>
      `).join('')
    : `<div class="${styles.chartTooltipEmpty}">No data</div>`

  return `
    <div class="${styles.chartTooltip}">
      <div class="${styles.chartTooltipHeader}">${dateString}</div>
      <div class="${styles.chartTooltipBody}">
        ${rowsMarkup}
      </div>
    </div>
  `
}

function round5 (x, up = true) { return up ? Math.ceil(x / 5) * 5 : Math.floor(x / 5) * 5 }

export const makeColumns = function makeColumns (
  translatedProps = [
    {
      type: 'date',
      label: 'Time',
      role: 'domain',
    },
    {
      type: 'string',
      role: 'tooltip',
      p: { html: true },
    },
    {
      type: 'number',
      label: 'Speed',
      role: 'data',
    },
    {
      type: 'number',
      label: 'Ambient Temp',
      role: 'data',
    },
    {
      type: 'number',
      label: 'Start Temp',
      role: 'data',
    },
  ]
) {
  return ([
    ...translatedProps,
  ])
}

export const makeRows = function makeRows (
  sensorData,
  device,
  dataType,
  tempConv,
  tempStart,
  translatedProps = {
    temperature: 'Ambient Temperature',
    temperatureStart: 'Start Temperature',
    speed: 'Speed',
  }
) {
  const rows = []
  if (
    sensorData.length > 0
  ) {
    for (let i = 0; i < sensorData.length; i += 1) {
      let stateVar = Math.round(sensorData[i][dataType] / (device.physical.rpmMultiplier || 1))
      if (device.physical.rpmSensor === 'OrchardRiteAutometer9117') {
        stateVar = Math.round(rpmOrchardRiteAutometer9117(stateVar))
      } else if (device.physical.offRpm && device.physical.highRpm) {
        const stateVarTemp = rpmToState(
          stateVar,
          device.physical.offRpm,
          device.physical.highRpm
        )
        switch (stateVarTemp) {
          case 'OFF':
            stateVar = 0
            break
          case 'LOW':
            stateVar = 1
            break
          case 'HIGH':
            stateVar = 2
            break
          default:
            stateVar = 0
            break
        }
      }
      if (!stateVar && stateVar !== 0) {
        stateVar = 0
      }
      let tempStartLocal = tempStart
      if ((sensorData[i]['200'] || sensorData[i]['200'] === 0) && !isNaN(sensorData[i]['200'])) {
        tempStartLocal = fromC(sensorData[i]['200'], tempConv, 1)
      }
      if (sensorData[i]['128'] || sensorData[i]['128'] === 0) {
        const temperature = fromC(sensorData[i]['128'], tempConv, 1)
        const combined = buildTooltip(sensorData[i].date, [
          {
            label: translatedProps.temperature,
            value: formatTooltipValue(temperature),
          },
          {
            label: translatedProps.temperatureStart,
            value: formatTooltipValue(tempStartLocal),
          },
          {
            label: translatedProps.speed,
            value: formatTooltipValue(stateVar),
          },
        ])
        rows.push(
          [
            new Date(sensorData[i].date),
            combined,
            stateVar,
            temperature,
            tempStartLocal,
          ]
        )
      }
    }
  }
  return rows
}

const ChartWindMachine = function ChartWindMachine ({
  sensorData,
  metricReady,
  device,
  dataType,
  intl: {
    formatMessage,
  },
  tempConv,
  isLoading = false,
}) {
  if (!metricReady) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }

  const applicationSettings = device.application_settings || { settings: {} }
  const tempStart = fromC(applicationSettings.settings.tempStart, tempConv, 1)

  const rows = makeRows(
    sensorData,
    device,
    dataType,
    tempConv,
    tempStart,
    {
      temperature: formatMessage({
        id: 'sensor.temperature.ambient.short',
        defaultMessage: 'Temperature',
        description: 'Temperature',
      }),
      temperatureStart: formatMessage({
        id: 'setting.tempStart',
        defaultMessage: 'Start Temperature',
        description: 'Start Temperature',
      }),
      speed: formatMessage({
        id: 'sensor.speed',
        defaultMessage: 'Speed',
        description: 'Speed',
      }),
    }
  )

  if (rows.length < 1) {
    return (
      <NoChartData />
    )
  }

  const columns = makeColumns([
    {
      type: 'date',
      label: formatMessage({
        id: 'sensor.time.short',
        defaultMessage: 'Time',
        description: 'Time',
      }),
      role: 'domain',
    },
    {
      type: 'string',
      role: 'tooltip',
      p: { html: true },
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'sensor.speed',
        defaultMessage: 'Speed',
        description: 'Speed',
      }),
      role: 'data',
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'sensor.temperature.ambient.short',
        defaultMessage: 'Temperature',
        description: 'Temperature',
      }),
      role: 'data',
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'setting.tempStart',
        defaultMessage: 'Start Temperature',
        description: 'Start Temperature',
      }),
      role: 'data',
    },
  ])
  const dimensions = chartDimensions(window.innerWidth)

  let maxTemp = 120
  let minTemp = -20

  try {
    maxTemp = round5(fromC((_maxBy(sensorData, o => o['128']))['128'], tempConv, 1), true)
    minTemp = round5(fromC((_minBy(sensorData, o => o['128']))['128'], tempConv, 1), false)
  } catch (error) {
    console.error(error)
    return (
      <NoChartData />
    )
  }

  if (tempConv === 'f') {
    if (maxTemp < 100) { maxTemp = 100 }
    if (minTemp > 25) { minTemp = 25 }
  } else {
    if (maxTemp < 35) { maxTemp = 35 }
    if (minTemp > 0) { minTemp = 0 }
  }

  while (minTemp > (tempStart - 10)) {
    minTemp -= 10
  }

  const tempTickSize = (maxTemp - minTemp) / 5
  const tempTicks = [
    tempTickSize * 1 + minTemp,
    tempTickSize * 2 + minTemp,
    tempTickSize * 3 + minTemp,
    tempTickSize * 4 + minTemp,
    maxTemp,
  ]

  let maxState = 3500
  const minState = 0
  let stateTicks = [
    700,
    1400,
    2100,
    2800,
    3500,
  ]
  if (device.physical.offRpm && device.physical.highRpm) {
    maxState = 3
    stateTicks = [
      0,
      { v: 1, f: 'ON' },
      { v: 2, f: 'ON' },
      { v: 3, f: 'ON' },
    ]
  }

  const options = {
    title: 'Levels',
    hAxis: {
      slantedText: false,
      gridlines: {
        count: -1,
        units: {
          days: { format: ['M/d'] },
          hours: { format: ['M/d ha'] },
        },
      },
      minorGridlines: {
        units: {
          hours: { format: ['ha'] },
          minutes: { format: ['HH:mm a Z', ':mm'] },
        },
      },
    },
    series: {
      0: {
        targetAxisIndex: 0,
        color: '#23CE6B',
        lineWidth: 4,
      },
      1: {
        targetAxisIndex: 1,
        color: '#D00000',
        lineWidth: 4,
      },
      2: {
        targetAxisIndex: 1,
        color: '#50b0ff',
        lineWidth: 4,
      },
    },
    vAxes: {
      // Adds titles to each axis.
      0: {
        title: 'State',
        minValue: minState,
        maxValue: maxState,
        textPosition: 'in',
        gridlines: {
          count: 5,
        },
        viewWindow: {
          min: minState,
          max: maxState,
        },
        ticks: stateTicks,
      },
      1: {
        title: `Temp (${tempConv})`,
        minValue: minTemp,
        maxValue: maxTemp,
        textPosition: 'in',
        gridlines: {
          count: 5,
        },
        viewWindow: {
          min: minTemp,
          max: maxTemp,
        },
        ticks: tempTicks,
      },
    },
    chartArea: {
      left: 0,
      top: 0,
      width: '100%',
      height: '85%',
    },
    isStacked: false,
    legend: { position: 'bottom' },
    // This line makes the entire category's tooltip active.
    focusTarget: 'category',
    // Use an HTML tooltip.
    tooltip: {
      isHtml: true,
      trigger: 'focus',
    },
  }

  return (
    <div className={styles.chart}>
      {isLoading && (
        <div className={styles.chartLoader} aria-live='polite'>
          <Spinner name='double-bounce' fadeIn='none' />
        </div>
      )}
      <Chart
        chartType='LineChart'
        rows={rows}
        width={dimensions.width}
        height={dimensions.height}
        columns={columns}
        options={options}
        style={{
          margin: '0 auto',
          position: 'relative',
          top: '5px',
        }}
      />
    </div>
  )
}

ChartWindMachine.propTypes = {
  sensorData: PropTypes.arrayOf(PropTypes.object),
  metricReady: PropTypes.bool,
  device: PropTypes.object,
  dataType: PropTypes.string,
  tempConv: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
}

export default withAppStore(injectIntl(ChartWindMachine))
